import {
  PriceDecreasePossibility,
  PriceMode,
  PricingAlertAction,
  PricingAlertPriority,
  PricingAvailabilityUpdateResult,
  PricingDataListItem,
  PricingDataSearchResult,
  PricingRequestStatus,
  ProductStates,
} from '../apiTypes';

export const pricingDataListItemTemplate: PricingDataListItem = {
  ean: '1234567890123',
  sku: 'SKU-123',
  mpId: '1001',
  marketplaceCode: 'ALLEGRO_PL',
  offerStatus: ProductStates.PUBLISHED_LIVE,
  productName: 'Sample Product',
  partnerBrand: 'BrandName',
  partnerSupplier: 'SupplierName',
  partnerCustomField: 'CustomValue',
  categoryId: 'CAT-001',
  categoryName: 'Electronics',
  handlingTime: 2,
  onHold: false,
  stock: 50,
  vat: 23,
  mpCurrency: 'PLN',
  exchangeRate: 1,
  partnerB2cPrice: 100.00,
  recommendedRetailPrice: 120.00,
  minimumRecommendedRetailPrice: 100.00,
  forcedPrice: 110.00,
  currentCustomerPrice: 105.00,
  calculatedCustomerPrice: 105.00,
  calculatedMinimumCustomerPrice: 90.00,
  calculatedRegularCustomerPrice: 95.00,
  partnerMinRelativeMargin: 10,
  partnerMinAbsoluteMargin: 100.00,
  partnerRegularRelativeMargin: 11,
  partnerRegularAbsoluteMargin: 110.00,
  basePriceInMarketplaceCurrency: 100.00,
  partnerDeliveryCostWoVat: 10.00,
  partnerWarehouseCostWoVat: 5.00,
  partnerReturnCostWoVat: 2.00,
  partnerAdditionalFixFee: 0.54,
  scCategoryRelativeCommission: 5,
  mpRelativeCommissionWithVat: 8,
  mpRelativeCommissionWithoutVat: 7,
  mpAdditionalFixFee: 1.00,
  calcMinimumBasisPrice: 80.00,
  calcRegularBasisPrice: 85.00,
  calcPayoutPrice: 95.00,
  mpLowestPrice: 99.00,
  marketLowestPrice: 98.00,
  competitionCheaperOnMarketplace: false,
  marketCompetitionCheaper: false,
  priceDecreasePossibility: PriceDecreasePossibility.POSSIBLE,
  priceSpread: 5.00,
  priceMode: PriceMode.MP_LOWEST_FROM_PA_B2B_PRICE,
  decreaseByAbs: 1.00,
  pricingNote: 'Standard pricing',
  pricingRequestStatus: PricingRequestStatus.PRICING_CALCULATED,
  pricingRequestMessage: 'Trigger error message',
  pricingRequestCreatedAt: '2025-12-25T00:00:00',
  errorMessage: 'Calculation error message',
  originalB2bPrice: 70.00,
  originalB2bPriceVat: 16.10,
  partnerOriginalB2bPriceCurrency: 'PLN',
  alertsByPriority: {
    [PricingAlertPriority.CRITICAL]: ['Price above RRP threshold'],
    [PricingAlertPriority.MAJOR]: ['Price slightly above 30d average'],
    [PricingAlertPriority.MINOR]: ['Price above 7d average'],
  },
  afterAlertingPrice: 1150.00,
  alertAction: PricingAlertAction.HOLD,
};

function getMockAlerts(index: number): Pick<PricingDataListItem, 'alertsByPriority' | 'afterAlertingPrice' | 'alertAction'> {
  switch (index % 4) {
    case 0:
      return {
        alertsByPriority: {
          [PricingAlertPriority.CRITICAL]: ['Price above RRP threshold', 'Margin below minimum'],
        },
        afterAlertingPrice: 1100 + index,
        alertAction: PricingAlertAction.HOLD,
      };
    case 1:
      return {
        alertsByPriority: {
          [PricingAlertPriority.MAJOR]: ['Price slightly above 30d average', 'Price above 14d average'],
          [PricingAlertPriority.MINOR]: ['Price above 7d average', 'Small price deviation detected'],
        },
        afterAlertingPrice: 1050 + index,
        alertAction: PricingAlertAction.HOLD,
      };
    case 2:
      return {
        alertsByPriority: {
          [PricingAlertPriority.CRITICAL]: ['Price above RRP threshold', 'Margin below minimum'],
          [PricingAlertPriority.MAJOR]: ['Price slightly above 30d average', 'Price above 14d average'],
          [PricingAlertPriority.MINOR]: ['Price above 7d average', 'Small price deviation detected'],
        },
        afterAlertingPrice: 1150 + index,
        alertAction: PricingAlertAction.HOLD,
      };
    default:
      return {
        alertsByPriority: undefined,
        afterAlertingPrice: undefined,
        alertAction: undefined,
      };
  }
}

const pricingData: PricingDataListItem[] = [];
for (let index = 0; index < 25; index++) {
  pricingData.push({
    ...pricingDataListItemTemplate,
    ean: `EAN-${index}`,
    sku: `SKU-${index}`,
    mpId: `1000-${index}`,
    productName: `Product ${index}`,
    stock: index * 10,
    currentCustomerPrice: 100 + index,
    onHold: index % 5 === 0,
    competitionCheaperOnMarketplace: index % 3 === 0,
    scCategoryRelativeCommission: 2 + (index % 5),
    pricingNote: index % 2 === 0 ? `Note for ${index}` : '',
    ...getMockAlerts(index),
  });
}

export const mockGetPricingDataList: PricingDataSearchResult = {
  page: {
    page: 1,
    total: 1234,
    pageSize: 25,
    data: pricingData,
  },
  maxPricingRequestId: 12345,
};

export const mockApplyAvailabilityUpdate: PricingAvailabilityUpdateResult = {
  total: 2,
  updated: 1,
  failed: 1,
  report: '"product_id","details"\n"02","Product with ID 02 does not exist."\n',
};
